import React, {useEffect, useState, useRef } from "react";
import colorStyle from "assets/jss/common/colorStyle";
import { useLanguage } from "Utils/Translation/LanguageContext"; 
import { CallApi } from "Utils/Request";
import parse, { domToReact } from 'html-react-parser'

interface TranslationResponse {
    choices: {
        message: {
            content: string;
        };
    }[];
}

// Still have to map since backend only recognizes full language name :/
const languageCodeToNameMap: { [key: string]: string } = {
    en: 'English',
    ja: 'Japanese',
};

const CURRENT_TRANSLATION_VERSION = 'v1.1'; //Update whenever a translation db is changed
const cacheVersionKey = 'translationCacheVersion';

if(localStorage.getItem(cacheVersionKey) !== CURRENT_TRANSLATION_VERSION){
    console.log(localStorage.getItem(cacheVersionKey))
    clearTranslationCache();
    localStorage.setItem(cacheVersionKey, CURRENT_TRANSLATION_VERSION);
}

function loadCacheFromStorage(key: string): Map<string, string> {
    try {
        const storedCache = localStorage.getItem(key);
        if (storedCache) {
            return new Map(Object.entries(JSON.parse(storedCache)));
        }
    } catch (error) {
        console.error("Failed to load cache from localStorage", error);
    }
    return new Map();
}

function saveCacheToStorage(key: string, cache: Map<any, any>): void {
    try {
        const plainObject = Object.fromEntries(cache);
        localStorage.setItem(key, JSON.stringify(plainObject));
    } catch (error) {
        console.error("Failed to save cache to localStorage", error);
    }
}

const translationCache: Map<string, string> = loadCacheFromStorage('translationCache');
const loadedParts: Set<string> = new Set(JSON.parse(localStorage.getItem('loadedParts') || '[]'));

function saveLoadedParts() {
    localStorage.setItem('loadedParts', JSON.stringify(Array.from(loadedParts)));
}


export function useTranslator(text: string, targetLanguage?: string, part?: string, timer?: number, isDynamic?: boolean, sourceLanguage?: string): [string, boolean, string] {
    const { language: contextLanguage } = useLanguage();
    const userLanguage = targetLanguage || contextLanguage;

    const [translatedText, setTranslatedText] = useState<string | null>(null);
    const [isTranslating, setIsTranslating] = useState(false);
    const isMountedRef = useRef(true);

    const lowerCaseText = text ? text.toLowerCase() : text;

    useEffect(() => {
        isMountedRef.current = true;

        // --- BRANCH FOR DYNAMIC TRANSLATION ---
        if (isDynamic) {
            setIsTranslating(true);
            const targetLanguageName = languageCodeToNameMap[userLanguage] || 'English';
            const sourceLanguageName = languageCodeToNameMap[sourceLanguage] || 'English';
            if (!text || (typeof targetLanguageName === 'string' && targetLanguageName === sourceLanguageName)){
                setTranslatedText(text);
                setIsTranslating(false)
                return;
            }
            
            CallApi({
                url: 'users/translation',
                method: 'post',
                data: { content: lowerCaseText, language: targetLanguageName },
            })
            .then((res: TranslationResponse) => {
                if (isMountedRef.current) {
                    const dynamicText = res?.choices?.[0]?.message?.content;
                    setTranslatedText(dynamicText || text);
                }
            })
            .catch(error => {
                console.error(`Failed to dynamically translate "${text}"`, error);
                if (isMountedRef.current) setTranslatedText(text);
            })
            .finally(() => {
                if (isMountedRef.current) setIsTranslating(false);
            });

        // --- EXISTING BRANCH FOR STATIC TRANSLATION ---
        } else {
            if (!lowerCaseText || (typeof userLanguage === 'string' && userLanguage.includes('en'))) {
                setTranslatedText(text);
                return;
            }
            setTranslatedText(null);
            const partKey = `${userLanguage}::${part}`;
            const cacheKey = `${lowerCaseText}::${userLanguage}::${part}`;
            setTimeout(() => { 
                if (translationCache.has(cacheKey)) {
                    if (isMountedRef.current) setTranslatedText(translationCache.get(cacheKey)!);
                } else if (!loadedParts.has(partKey) && part) {
                    getDBTextBatch(part);
                } else {
                    if (isMountedRef.current) setTranslatedText(text);
                }
            }, timer ? timer : 0);
        }

        return () => {
            isMountedRef.current = false;
        };
    }, [text, userLanguage, part, isDynamic]);


    function getDBTextBatch(part: string) { 
        CallApi({
            url: `stocks/website_translations?language=${userLanguage}&part=${part}`,
            method: 'get',
        }).then((res: any[]) => {
            res.forEach(item => {
                const key = `${item.source?.toLowerCase()}::${userLanguage}::${part}`;
                translationCache.set(key, item.destination);
            });
            loadedParts.add(`${userLanguage}::${part}`);
            saveCacheToStorage('translationCache', translationCache);
            saveLoadedParts();

            const cacheKey = `${lowerCaseText}::${userLanguage}::${part}`;
            if (translationCache.has(cacheKey) && isMountedRef.current) {
                setTranslatedText(translationCache.get(cacheKey)!);
            } else if (isMountedRef.current) {
                setTranslatedText(text);
            }
        }).catch(() => {
            if (isMountedRef.current) setTranslatedText(text);
        });
    }

    if (isTranslating) {
        return [text, isTranslating, userLanguage as string];
    }
    if (translatedText) {
        return [translatedText, isTranslating, userLanguage as string];
    }
    return [text, isTranslating, userLanguage as string];
}

export default function WebTranslateText ({
    text,
    sourceLanguage,
    language,
    part,
    timer,
    isDynamic,
} : {
    text: string,
    sourceLanguage?: string,
    language?: string,
    part?: string,
    timer?: number,
    isDynamic?: boolean,
}) {
    const [translatedText, isTranslating] = useTranslator(text, language, part, timer, isDynamic, sourceLanguage)

    const options = {
        replace: (domNode: any) => {
          if (domNode.type === 'tag' && domNode.name === 'info') {
            return (
              <span style={{ fontWeight: 'bold', color: colorStyle.infoColor }}>
                {domToReact(domNode.children, options)}
              </span>
            );
          }
          if (domNode.type === 'tag' && domNode.name === 'href') {
            const url = domNode.attribs?.to
            return (
                <a href={url} target='_blank' style={{ color: colorStyle.primaryColor, textDecoration: 'underline' }}>
                  {domToReact(domNode.children, options)}
                </a>
            )
          }
        }
      };

    if(isTranslating){
        return <span>...</span>;
    }
    if (translatedText){
        return <>{parse(translatedText || '', options)}</>
    }
    return <>{parse(text||'', options)}</>
}

export function clearTranslationCache(){
    localStorage.removeItem('translationCache');
    localStorage.removeItem('loadedParts');
    console.log('Translation cache cleared');
}

(window as any).clearTranslationCache = clearTranslationCache;